/**
 * NotificationBell — top-right bell with unread count + dropdown.
 *
 * Sources of truth:
 *   - GET /api/sales/notifications?unreadOnly=true   (initial load + 60s poll)
 *   - WS event 'notification:new'                    (instant arrival)
 *   - POST /api/sales/notifications/ack              (when dropdown opens)
 *
 * Item rendering is type-aware (lead.created / lead.replied / etc.).
 * Clicking an item navigates to the relevant lead (sales workspace).
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  type Notification as NotificationDto,
  Routes,
} from '@tg-leads/contracts';
import { io, type Socket } from 'socket.io-client';

import { api, getToken } from '../lib/api';

export function NotificationBell() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const sockRef = useRef<Socket | null>(null);

  const unread = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () =>
      api.get<{ items: NotificationDto[] }>(
        `${Routes.sales.notifications}?unreadOnly=true&limit=50`,
      ),
    refetchInterval: 60_000,
  });

  // Independent WS subscription so the bell works on every page, even when
  // the sales workspace's useLeadRealtime hook isn't mounted.
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const sock = io({ auth: { token }, transports: ['websocket', 'polling'] });
    sockRef.current = sock;
    sock.on('notification:new', () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
    });
    return () => {
      sock.disconnect();
      sockRef.current = null;
    };
  }, [qc]);

  const ack = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ ackedCount: number }>(Routes.sales.notificationsAck, {
        notificationIds: ids,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['notifications', 'unread'] }),
  });

  const items = unread.data?.items ?? [];
  const count = items.length;

  function onOpen() {
    setOpen((v) => {
      const next = !v;
      // Ack everything visible when the user opens — they've "seen" it.
      if (next && items.length > 0) {
        ack.mutate(items.map((i) => i.id));
      }
      return next;
    });
  }

  return (
    <div className="relative">
      <button
        onClick={onOpen}
        className="relative p-1 hover:bg-slate-100 rounded text-slate-600"
        title="通知"
        aria-label="通知"
      >
        <span className="text-xl leading-none">🔔</span>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 w-80 bg-white border rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
            <div className="px-3 py-2 border-b text-xs font-semibold text-slate-600">
              通知（{count} 条未读）
            </div>
            {items.length === 0 ? (
              <div className="px-3 py-6 text-xs text-slate-400 text-center">
                没有新通知
              </div>
            ) : (
              <ul className="divide-y">
                {items.map((n) => (
                  <NotifItem
                    key={n.id}
                    n={n}
                    onClick={() => {
                      const leadId = (n.payloadJson as { leadId?: string })
                        .leadId;
                      if (leadId) {
                        nav(`/sales/leads?id=${leadId}`);
                        setOpen(false);
                      }
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function NotifItem({
  n,
  onClick,
}: {
  n: NotificationDto;
  onClick: () => void;
}) {
  const p = n.payloadJson as Record<string, string | undefined>;
  const summary = (() => {
    switch (n.type) {
      case 'lead.created':
        return `新线索：目标用户 ${p.targetUserId ?? ''}${p.confidence ? `（置信度 ${Number(p.confidence).toFixed(2)}）` : ''}`;
      case 'lead.replied':
        return `对方回复：${(p.textPreview ?? '').slice(0, 60)}`;
      case 'lead.assigned':
        return `线索已分配：${p.leadId ?? ''}`;
      case 'lead.closed':
        return `线索已结案：${p.outcome === 'closed_won' ? '成单' : p.outcome === 'closed_lost' ? '未成单' : (p.outcome ?? '')}`;
      case 'system.alert':
        return p.message ?? '系统提醒';
      default:
        return n.type;
    }
  })();
  return (
    <li
      onClick={onClick}
      className="px-3 py-2 hover:bg-slate-50 cursor-pointer"
    >
      <div className="text-xs text-slate-700 break-words">{summary}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">
        {new Date(n.createdAt).toLocaleString()}
      </div>
    </li>
  );
}
